import type {
    NotificationTemplateEntry,
    NotificationTemplatePreviewResource,
    NotificationTestMailResource,
} from './types';

/**
 * De berichten die een bedrijf automatisch aan zijn klanten stuurt, zoals de
 * editor in Instellingen ze ziet.
 *
 * Een bericht (`key`) gaat per e-mail en soms ook per sms; per kanaal is er één
 * tekst. Die tekst is de **standaardtekst** van Smartkeur, of de **eigen tekst**
 * van het bedrijf. Variabelen staan in de tekst als `{{ client_name }}` en heten
 * op het scherm zoals de API ze noemt ("Klantnaam").
 */

export const MESSAGE_CHANNELS = ['mail', 'sms'] as const;

export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

export function isMessageChannel(value: string): value is MessageChannel {
    return (MESSAGE_CHANNELS as readonly string[]).includes(value);
}

/** De tekst van één bericht op één kanaal. */
export interface MessageTemplate {
    key: string;
    keyLabel: string;
    channel: MessageChannel;
    channelLabel: string;
    /** `null` bij een sms: die heeft geen onderwerp. */
    subject: string | null;
    body: string;
    /** Het bedrijf gebruikt een eigen tekst in plaats van de standaardtekst. */
    customised: boolean;
    /** De eigen tekst, om hem weg te halen; alleen als `customised`. */
    uuid: string | null;
    updatedAt: string | null;
    defaultSubject: string | null;
    defaultBody: string;
    /** Namen zoals ze in de tekst staan, in de volgorde van de API. */
    variables: string[];
    variableLabels: Record<string, string>;
    maxBodyLength: number;
}

export function toMessageTemplate(entry: NotificationTemplateEntry): MessageTemplate | null {
    if (!isMessageChannel(entry.channel)) {
        return null;
    }

    const customised = entry.source === 'tenant';

    return {
        key: entry.key,
        keyLabel: entry.key_label,
        channel: entry.channel,
        channelLabel: entry.channel_label,
        subject: entry.channel === 'mail' ? (entry.subject ?? '') : null,
        body: entry.body,
        customised,
        uuid: customised ? entry.uuid : null,
        updatedAt: customised ? entry.updated_at : null,
        defaultSubject: entry.channel === 'mail' ? (entry.default_subject ?? '') : null,
        defaultBody: entry.default_body,
        variables: entry.variables,
        variableLabels: entry.variable_labels,
        maxBodyLength: entry.max_body_length,
    };
}

/** Een bericht met al zijn kanalen, e-mail eerst. */
export interface ClientMessage {
    key: string;
    label: string;
    channels: MessageTemplate[];
    customised: boolean;
}

export function groupMessages(templates: readonly MessageTemplate[]): ClientMessage[] {
    const byKey = new Map<string, MessageTemplate[]>();

    for (const template of templates) {
        byKey.set(template.key, [...(byKey.get(template.key) ?? []), template]);
    }

    return [...byKey.entries()].map(([key, channels]) => {
        const sorted = [...channels].sort(
            (a, b) => MESSAGE_CHANNELS.indexOf(a.channel) - MESSAGE_CHANNELS.indexOf(b.channel),
        );

        return {
            key,
            label: sorted[0]?.keyLabel ?? key,
            channels: sorted,
            customised: sorted.some((channel) => channel.customised),
        };
    });
}

/** Wat je schrijft: het onderwerp (alleen bij e-mail) en de tekst. */
export interface MessageDraft {
    subject: string | null;
    body: string;
}

export interface MessagePreview {
    subject: string | null;
    body: string;
    html: string | null;
    text: string | null;
    values: Record<string, string>;
}

export function toMessagePreview(resource: NotificationTemplatePreviewResource): MessagePreview {
    return {
        subject: resource.subject,
        body: resource.body,
        html: resource.html,
        text: resource.text,
        values: resource.values,
    };
}

export interface TestMailResult {
    recipient: string;
    usedPlatformFallback: boolean;
}

export function toTestMailResult(resource: NotificationTestMailResource): TestMailResult {
    return {
        recipient: resource.recipient,
        usedPlatformFallback: resource.used_platform_fallback,
    };
}

/* -------------------------------------------------------------------------- */
/* Variabelen                                                                  */
/* -------------------------------------------------------------------------- */

/** `{{ client_name }}`: zo staat een variabele in de tekst. */
export function placeholderFor(name: string): string {
    return `{{ ${name} }}`;
}

const PLACEHOLDER = /^\{\{\s*([a-z][a-z0-9_]*)\s*\}\}$/;
const BRACES = /\{\{[\s\S]*?\}\}/g;
const STRAY_BRACES = /\{\{\s*[^\s{}]{0,30}|[^\s{}]{0,30}\s*\}\}/gu;

/**
 * Wat er tussen accolades staat en geen variabele van dit bericht is, zoals het
 * er staat: `{{ adres }}`, `{{ Klantnaam }}`, een `{{` zonder `}}`.
 *
 * Dezelfde regel als de API (`TemplateRenderer::assertPlaceholdersAllowed`), zodat
 * het scherm een fout al tijdens het typen laat zien. Opslaan controleert het
 * daarna nog een keer.
 */
export function refusedPlaceholders(text: string, allowed: readonly string[]): string[] {
    const refused: string[] = [];

    for (const [fragment] of text.matchAll(BRACES)) {
        const name = PLACEHOLDER.exec(fragment)?.[1];

        if (name === undefined) {
            refused.push(fragment.trim());
        } else if (!allowed.includes(name)) {
            refused.push(placeholderFor(name));
        }
    }

    for (const [fragment] of text.replace(BRACES, ' ').matchAll(STRAY_BRACES)) {
        refused.push(fragment.trim());
    }

    return [...new Set(refused)];
}

/* -------------------------------------------------------------------------- */
/* Sms-lengte                                                                  */
/* -------------------------------------------------------------------------- */

/** De tekens van de standaard sms-tekenset (GSM 03.38). */
const GSM_BASIC = new Set(
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
);

/** Tekens die in die tekenset twee plaatsen kosten. */
const GSM_EXTENDED = new Set('^{}\\[~]|€\f');

export interface SmsLength {
    /** Tekens zoals een mens ze telt. */
    characters: number;
    /** Hoeveel sms-berichten de telefoonmaatschappij ervan maakt. */
    parts: number;
    /** `unicode` als er een teken in staat buiten de gewone tekenset (zoals ë). */
    encoding: 'gsm' | 'unicode';
}

/**
 * Hoeveel sms-berichten een tekst kost.
 *
 * Eén sms is 160 tekens uit de gewone tekenset, of 70 zodra er één teken buiten
 * valt (een ë, een emoji). Een langere tekst gaat in delen van 153 of 67 tekens.
 */
export function smsLength(text: string): SmsLength {
    const characters = [...text].length;

    if (text === '') {
        return { characters: 0, parts: 0, encoding: 'gsm' };
    }

    let units = 0;
    let gsm = true;

    for (const character of text) {
        if (GSM_BASIC.has(character)) {
            units += 1;
        } else if (GSM_EXTENDED.has(character)) {
            units += 2;
        } else {
            gsm = false;
            break;
        }
    }

    if (gsm) {
        return { characters, parts: units <= 160 ? 1 : Math.ceil(units / 153), encoding: 'gsm' };
    }

    return {
        characters,
        parts: text.length <= 70 ? 1 : Math.ceil(text.length / 67),
        encoding: 'unicode',
    };
}
